import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { JobArchivingReasonEditModal } from "@/pages/settings/recruit/job-archiving-reasons/widgets/JobArchivingReasonEditModal.tsx";
import {
  useJobArchivingReasonDelete,
  useJobArchivingReasons,
} from "@/queries/recruit/use-job-archiving-reasons.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiJobArchivingReason } from "@/types/recruit/job-archiving-reason.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export function JobArchivingReasons() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  useTitle(t("ats:job_archiving_reasons"), t("common:settings"));

  const { data: jobArchivingReasons, isLoading, isFetching } = useJobArchivingReasons();
  const jobArchivingReasonDelete = useJobArchivingReasonDelete();

  const columns = useMemo<ColumnDef<ApiJobArchivingReason>[]>(() => {
    return [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
    ];
  }, [jobArchivingReasons]);

  const handleEditOrCreate = useCallback(
    (reason: ApiJobArchivingReason | undefined = undefined) => {
      openModal(JobArchivingReasonEditModal, {
        jobArchivingReason: reason,
      });
    },
    []
  );

  const handleDelete = useCallback(async (reason: ApiJobArchivingReason) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await jobArchivingReasonDelete.mutateAsync(reason.id, {
            onSuccess() {
              analyticsService.trackEvent(analyticEvents.jobArchivingReasons.deleted, {
                [analyticProperties.id]: reason.id,
              });
            },
          });
        } catch {}
      },
    });
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.jobArchivingReasons.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ApiJobArchivingReason>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            void handleDelete(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDelete, handleEditOrCreate, t]
  );

  return (
    <Page>
      <Page.Header
        showBack={true}
        title={t("ats:job_archiving_reasons")}
        navigateTo={"/settings/recruitment-directories"}
      >
        <Stack>
          <Button
            onClick={() => handleEditOrCreate()}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("common:create_new")}
          </Button>
        </Stack>
      </Page.Header>
      <Page.Content>
        <Table
          renderRowAction={renderRowAction}
          data={jobArchivingReasons || []}
          columns={columns}
          isFetching={isFetching || jobArchivingReasonDelete.isPending}
          showSkeleton={isLoading}
          notFoundComponent={<EmptyStateNothingFound />}
          emptyStateComponent={<EmptyState />}
        />
      </Page.Content>
    </Page>
  );
}
